import { toKr, type Ore } from '@heivol-finance/calc';

/**
 * Currency-aware money formatting. All amounts are stored as integer øre/cents (×100);
 * every supported currency is 2-decimal, so the minor-unit storage is unchanged when the
 * display currency switches. The display currency is module-level state, set once after
 * the session authenticates (see App.tsx → setDisplayCurrency) so every page formats in the
 * chosen currency on first paint. Default stays DKK.
 */

/** Supported display currencies (all 2-decimal) and their formatting locales. */
export const SUPPORTED_CURRENCIES = ['DKK', 'EUR', 'USD', 'GBP', 'SEK', 'NOK'] as const;
export type CurrencyCode = (typeof SUPPORTED_CURRENCIES)[number];

const LOCALE_BY_CURRENCY: Record<CurrencyCode, string> = {
  DKK: 'da-DK',
  EUR: 'de-DE',
  USD: 'en-US',
  GBP: 'en-GB',
  SEK: 'sv-SE',
  NOK: 'nb-NO',
};

export function isSupportedCurrency(code: string): code is CurrencyCode {
  return (SUPPORTED_CURRENCIES as readonly string[]).includes(code);
}

/** Locale for a currency code (falls back to da-DK for anything unexpected). */
export function localeForCurrency(code: string): string {
  return isSupportedCurrency(code) ? LOCALE_BY_CURRENCY[code] : 'da-DK';
}

// ── Module state + derived formatters ───────────────────────────────────────────
let currency: CurrencyCode = 'DKK';
let locale: string = LOCALE_BY_CURRENCY.DKK;

let money: Intl.NumberFormat;
let moneyPrecise: Intl.NumberFormat;
let num: Intl.NumberFormat;
let pct: Intl.NumberFormat;
let symbol: string;

function rebuild(): void {
  money = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  });
  moneyPrecise = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  num = new Intl.NumberFormat(locale);
  pct = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 });
  symbol = deriveSymbol();
}

/** Derive the currency symbol via formatToParts (e.g. "kr.", "€", "$"); fall back to the code. */
function deriveSymbol(): string {
  try {
    const parts = new Intl.NumberFormat(locale, { style: 'currency', currency }).formatToParts(1);
    return parts.find((p) => p.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
}

/**
 * Set the whole app's display currency (and its derived locale). Call once after auth,
 * before rendering the authed app, so the first paint formats correctly.
 */
export function setDisplayCurrency(code: string): void {
  currency = isSupportedCurrency(code) ? code : 'DKK';
  locale = localeForCurrency(currency);
  rebuild();
}

export function currencyCode(): CurrencyCode {
  return currency;
}

export function currencySymbol(): string {
  return symbol;
}

// Initialise with the default (DKK).
rebuild();

/** Format integer øre as the current display currency (e.g. 264000000 → "2.640.000 kr."). */
export function formatOre(ore: Ore, precise = false): string {
  return (precise ? moneyPrecise : money).format(toKr(ore));
}

export function formatNumber(n: number): string {
  return num.format(n);
}

export function formatPercent(fraction: number): string {
  return pct.format(fraction);
}

/**
 * Group an integer øre amount for display in a money input, using the current locale but
 * without the currency symbol (e.g. 4200000 → "42.000" in da-DK, "42,000" in en-US).
 * Whole units only when the øre are a round amount; otherwise show the 2 decimal places.
 */
export function groupOre(ore: Ore): string {
  const kroner = toKr(ore);
  const hasFraction = Math.round(kroner * 100) % 100 !== 0;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(kroner);
}

/**
 * Parse a money amount string into integer øre, robust to BOTH da-DK ("1.234,56") and
 * en-US ("1,234.56") input. Strips the currency symbol/spaces, then:
 *  - if BOTH "." and "," appear, the LAST one is the decimal separator (the other groups);
 *  - if only one separator appears and it has exactly 3 trailing digits, it's a thousands
 *    separator (e.g. "1.234" / "1,234" → 1234); otherwise it's the decimal separator.
 */
export function parseKrToOre(input: string): Ore {
  // Keep digits, separators and a leading sign; drop symbols, letters and spaces.
  let s = input.replace(/[^0-9.,-]/g, '').trim();
  if (!s) return 0;

  const neg = s.startsWith('-');
  s = s.replace(/-/g, '');

  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');
  const dotCount = (s.match(/\./g) ?? []).length;
  const commaCount = (s.match(/,/g) ?? []).length;

  // A lone separator TYPE reads as a decimal point only when it occurs exactly once and does
  // NOT look like a thousands group (i.e. not exactly 3 trailing digits). Repeated separators
  // of the same type (e.g. "2.640.000") are always grouping.
  const looksDecimal = (last: number, count: number): boolean =>
    count === 1 && s.length - last - 1 !== 3;

  let normalized: string;
  if (lastDot !== -1 && lastComma !== -1) {
    // Both present → the LAST one is the decimal separator; strip the other (grouping).
    normalized =
      lastComma > lastDot ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  } else if (lastComma !== -1) {
    normalized = looksDecimal(lastComma, commaCount) ? s.replace(',', '.') : s.replace(/,/g, '');
  } else if (lastDot !== -1) {
    normalized = looksDecimal(lastDot, dotCount) ? s : s.replace(/\./g, '');
  } else {
    normalized = s;
  }

  const kroner = Number.parseFloat(normalized);
  if (!Number.isFinite(kroner)) return 0;
  return Math.round((neg ? -kroner : kroner) * 100);
}
